import { useRef } from 'preact/hooks';
import type { RefObject } from 'preact';
import { Building2, Check, ChevronsUpDown } from 'lucide-preact';
import { Menu } from '../overlays/Menu';
import { useFloatingState } from '../overlays/Popover';
import { useHoldSidebarWhile } from './sidebarHold';
import { currentUser, switchCompany } from '@/stores/auth';
import { toast } from '@/stores/toasts';
import { cn } from '@/lib/cn';

export interface WorkspaceSwitcherProps {
  collapsed?: boolean;
}

/**
 * WorkspaceSwitcher — header del sidebar al estilo Linear/Notion/Stripe.
 *
 * - 1 empresa: lockup `[avatar] [nombre]` read-only.
 * - 2+ empresas: mismo lockup + chevron, click → dropdown con la lista
 *   de empresas, marcando la activa con check.
 *
 * Variantes:
 *   - Expanded: avatar + nombre + chevron.
 *   - Collapsed (sidebar 56px): solo avatar.
 *
 * Empresa "avatar" actual = inicial primera letra sobre `bg-brand-tint`
 * + `text-brand`.  Si en el futuro hay `company.logo_url` se renderiza
 * la imagen en su lugar.
 */
export function WorkspaceSwitcher({ collapsed }: WorkspaceSwitcherProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { open, toggle, close } = useFloatingState(false);
  useHoldSidebarWhile(open);
  const user = currentUser.value;
  if (!user) return null;

  const companies = user.companies ?? [];
  const current = companies.find((c) => c.id === user.current_company_id);
  const companyName = current?.name ?? user.company_name ?? 'Sin empresa activa';
  const hasMultiple = companies.length > 1;
  const initial = companyName.trim().charAt(0).toUpperCase() || '·';

  // Avatar de workspace — bloque inverted (text-canvas sobre fg-strong)
  // estilo Vercel/Linear.  Antes era `bg-brand text-brand-contrast` que
  // pintaba un cuadrado azul fluorescente: chocaba con el resto del
  // sidebar y se confundía con el brand de Onmi en sí (no del workspace
  // del cliente).
  const Avatar = (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-semibold',
        'bg-fg-strong text-canvas text-2xs',
      )}
    >
      {initial}
    </span>
  );

  // Nombre + chevron (solo expanded).  Sin kicker "Empresa" — el contexto
  // del header del sidebar ya implica "esto es la empresa activa".
  const Label = !collapsed ? (
    <>
      <span className="text-13 text-fg-strong min-w-0 flex-1 truncate font-medium">
        {companyName}
      </span>
      {hasMultiple ? (
        <ChevronsUpDown size={13} strokeWidth={2} className="text-fg-faint shrink-0" />
      ) : null}
    </>
  ) : null;

  // Cuando hay 1 sola empresa, renderizar un `<div>` (no `<button disabled>`)
  // para evitar la semántica "esto es interactivo pero deshabilitado"
  // que confunde al SR + tab-order.
  if (!hasMultiple) {
    return (
      <div
        aria-label={`Empresa activa: ${companyName}`}
        className={cn(
          'flex w-full items-center text-left',
          collapsed
            ? 'justify-center rounded-md p-1'
            : 'gap-3 rounded-md border border-transparent px-3 py-1.5',
        )}
      >
        {Avatar}
        {Label}
      </div>
    );
  }

  const Trigger = (
    <button
      ref={triggerRef}
      type="button"
      onClick={toggle}
      aria-label={`Cambiar empresa, actual: ${companyName}`}
      aria-haspopup="menu"
      aria-expanded={open}
      className={cn(
        'group/workspace relative flex w-full items-center text-left outline-none',
        'duration-med ease-smooth transition-[background-color,border-color]',
        'focus-visible:shadow-focus focus-visible:ring-brand focus-visible:ring-1',
        collapsed
          ? 'hover:bg-surface-2 justify-center rounded-md p-1'
          : cn(
              'gap-3 rounded-md border px-3 py-1.5',
              open
                ? 'border-border-subtle bg-surface-2'
                : 'hover:border-border-subtle hover:bg-surface-2 border-transparent',
            ),
      )}
    >
      {Avatar}
      {Label}
    </button>
  );

  return (
    <>
      {Trigger}
      <Menu
        open={open}
        onClose={close}
        anchor={triggerRef as RefObject<HTMLElement>}
        placement={collapsed ? 'right' : 'bottom-start'}
        matchAnchorWidth={!collapsed}
        sections={[
          {
            id: 'companies',
            label: 'Cambiar empresa',
            items: companies.map((company) => ({
              id: `company-${company.id}`,
              label: company.name,
              icon: company.id === user.current_company_id ? Check : Building2,
              disabled: company.id === user.current_company_id,
              onSelect: () => {
                void switchCompany(company.id)
                  .then(() => toast.success(`Empresa activa: ${company.name}`))
                  .catch((error: Error) => toast.error(error.message));
              },
            })),
          },
        ]}
      />
    </>
  );
}
